import { useEffect, useState } from 'react';

/**
 * Whether the page is in front of the user, with a grace period so a quick
 * tab switch does not tear down and rebuild whatever depends on it.
 *
 * Reports `false` only after the document has been hidden for `graceMs`, and
 * `true` again the moment it is shown. Meant for connections that exist to
 * paint the screen: a socket held open behind a hidden tab is a broadcast
 * recipient doing work nobody sees.
 */
export function usePageVisible(graceMs = 30_000): boolean {
    const [visible, setVisible] = useState<boolean>(() =>
        typeof document === 'undefined'
            ? true
            : document.visibilityState !== 'hidden',
    );

    useEffect(() => {
        if (typeof document === 'undefined') return;
        let timer: ReturnType<typeof setTimeout> | null = null;

        const onChange = () => {
            if (document.visibilityState === 'hidden') {
                if (timer !== null) return;
                timer = setTimeout(() => {
                    timer = null;
                    setVisible(false);
                }, graceMs);
                return;
            }
            if (timer !== null) {
                clearTimeout(timer);
                timer = null;
            }
            setVisible(true);
        };

        onChange();
        document.addEventListener('visibilitychange', onChange);
        return () => {
            document.removeEventListener('visibilitychange', onChange);
            if (timer !== null) clearTimeout(timer);
        };
    }, [graceMs]);

    return visible;
}
